import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { Flex, Text } from '@atlaskit/primitives/compiled';
import { ColorPicker, isReadable, useBuiltInColorMap } from '@atlassian/color-picker';
import { useProductDependencies } from '@atlassian/company-hub-common';

import { CARDS_EXTENSION_CONFIG_BACKGROUND_ANALYTIC_SOURCE } from '../../../../constants';
import type { CardsActions } from '../../../../controllers/use-cards-extension-parameters-and-actions/types';

import { messages } from './messages';

type BackgroundColorFieldProps = {
	backgroundColor: string;
	isCompanyHub: boolean;
	textColor: string;
	updateMainFormField: CardsActions['updateMainFormField'];
};

export const BackgroundColorField = ({
	backgroundColor,
	textColor,
	isCompanyHub,
	updateMainFormField,
}: BackgroundColorFieldProps): React.JSX.Element => {
	const { isSSRProductInDarkMode } = useProductDependencies();
	const { branding } = useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap } = branding ?? {};
	const { backgroundColorMap } = useBuiltInColorMap(isSSRProductInDarkMode);
	const isReadableBackgroundColor = (color: string) => isReadable(color, textColor);

	const handleChange = useCallback(
		(color: string) => {
			updateMainFormField({ backgroundColor: color });
		},
		[updateMainFormField],
	);

	return (
		<Flex justifyContent="space-between" alignItems="center">
			<Text color="color.text">
				<FormattedMessage {...messages.backgroundColorLabel} />
			</Text>
			<ColorPicker
				analyticsSource={CARDS_EXTENSION_CONFIG_BACKGROUND_ANALYTIC_SOURCE}
				builtInColorMap={
					hideDefaultColorPalette && brandBackgroundColorMap
						? brandBackgroundColorMap
						: backgroundColorMap
				}
				isReadableColor={isReadableBackgroundColor}
				onChange={handleChange}
				value={backgroundColor}
				pickerTitle={messages.backgroundColorLabel}
				showCustomPalette={!!isCompanyHub}
			/>
		</Flex>
	);
};
